// Conversations (agent side) and the public visitor widget — ONE shared conversation store:
//   visitor message -> bot reply (simulateBotReply) -> escalation -> agent queue -> agent reply ->
//   visitor widget polls and shows it; lead capture writes to the same Lead Management store.
import { conversationsStore, botsStore, usersStore, domainsStore, botPublicView, defaultBotFor, leadCaptureFor } from "../core";
import { availabilityFor } from "../hours";
import { simulateBotReply, detectIntent } from "../botEngine";
import { consumeAiReply } from "../ai";
import { createLeadRecord, scoreLead } from "./leads";
import { ok, fail, validationFail, genId, nowIso, randomCode } from "../helpers";
import { collect, emailError, firstMessage, lengthError, nameError, phoneError } from "../validate";

const STATUSES = ["Open", "Pending", "Escalated", "Closed", "Resolved"];
const PRIORITIES = ["low", "medium", "high"];

const msg = (from, text, extra = {}) => ({ id: genId("msg"), from, text, createdAt: nowIso(), ...extra });

function agentName(id) {
  return usersStore.all().find((u) => u.id === id)?.name || null;
}

const convoView = (c) => ({ ...c, assignedAgentName: c.assignedAgentId ? agentName(c.assignedAgentId) : null });

/** What a website visitor may see: no internal notes/tags, no agent-only system events. */
function widgetView(c) {
  return {
    id: c.id, status: c.status, customer: c.customer, visitorName: c.visitorName || "", visitorEmail: c.visitorEmail || "",
    leadCaptured: !!c.leadId, assignedAgentName: c.assignedAgentId ? agentName(c.assignedAgentId) : null, updatedAt: c.updatedAt,
    messages: c.messages
      .filter((m) => m.from !== "system" || m.public)
      .map((m) => ({ id: m.id, from: m.from, text: m.text, createdAt: m.createdAt, ...(m.agentName ? { agentName: m.agentName } : {}) }))
  };
}

function findConvo(tenantId, id) {
  const all = conversationsStore.all();
  return { all, convo: all.find((c) => c.id === id && c.tenantId === tenantId) };
}

/** Public bot view + whether the team is open right now (Settings → Business hours). */
const publicBot = (bot) => ({ ...botPublicView(bot), availability: availabilityFor(bot.tenantId) });

function escalate(convo, { reason, systemText, botText }) {
  const hours = availabilityFor(convo.tenantId);
  if (!hours.open) {
    convo.escalatedOffHours = true;
    botText = hours.mode === "offline" && hours.offlineMessage
      ? hours.offlineMessage
      : `${botText} We are outside business hours, so you are in the queue and an agent will reply when we reopen.`;
  }
  convo.status = "Escalated";
  convo.priority = "high";
  convo.escalatedAt = nowIso();
  convo.escalationReason = reason;
  convo.messages.push(msg("system", systemText));
  convo.messages.push(msg("bot", botText, { meta: { escalated: true, reason } }));
}

function hostAllowed(tenantId, origin) {
  let host = "";
  try { host = new URL(origin).hostname.toLowerCase(); } catch (e) { host = String(origin || "").toLowerCase(); }
  if (!host || host === "localhost" || host === "127.0.0.1") return true;
  const list = domainsStore.all()[tenantId] || [];
  if (list.length === 0) return false;
  return list.some((d) => host === d.domain || host.endsWith(`.${d.domain}`));
}

export const chatRoutes = [
  // ---------------------------------------------------------------- agent-side conversations
  {
    method: "GET", pattern: /^\/api\/conversations$/, permission: "conversations:read",
    handler: ({ tenantId, userId, query, scenario }) => {
      let items = scenario === "empty" ? [] : conversationsStore.all().filter((c) => c.tenantId === tenantId);
      const search = (query.get("search") || "").trim().toLowerCase();
      if (search) items = items.filter((c) => c.customer.toLowerCase().includes(search));
      const status = query.get("status");
      if (status) items = items.filter((c) => c.status === status);
      const assigned = query.get("assigned");
      if (assigned === "unassigned") items = items.filter((c) => !c.assignedAgentId);
      if (assigned === "me") items = items.filter((c) => c.assignedAgentId === userId);
      items = [...items].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      return ok(200, items.map(convoView), { total: items.length, page: 1, pageSize: 20, tenantId, waitAlertMinutes: availabilityFor(tenantId).waitAlertMinutes });
    }
  },
  {
    // Assignable agents (active, non-viewer members). Any agent role may read this so the
    // Assignment Control works without the admin-only users:manage permission.
    method: "GET", pattern: /^\/api\/agents$/, permission: "conversations:read",
    handler: ({ tenantId }) => {
      const items = usersStore.all()
        .filter((u) => u.tenantId === tenantId && u.status === "Active" && u.teamRole !== "Viewer")
        .map((u) => ({ id: u.id, name: u.name, role: u.teamRole, status: /^(Just now|\d+ min ago)$/.test(u.lastActive || "") ? "online" : "away" }));
      return ok(200, items, { total: items.length });
    }
  },
  {
    method: "GET", pattern: /^\/api\/conversations\/([\w-]+)$/, permission: "conversations:read",
    handler: ({ tenantId, match }) => {
      const item = conversationsStore.all().find((c) => c.id === match[1]);
      if (!item) return fail(404, "NOT_FOUND", "Conversation not found.");
      if (item.tenantId !== tenantId) return fail(403, "TENANT_MISMATCH", "This record belongs to a different workspace.", { tenantId });
      return ok(200, convoView(item));
    }
  },
  {
    method: "PATCH", pattern: /^\/api\/conversations\/([\w-]+)$/, permission: "conversations:write",
    handler: ({ tenantId, userId, match, body }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      if (body.status !== undefined) {
        if (!STATUSES.includes(body.status)) return validationFail({ status: "Invalid conversation status." }, "Invalid conversation status.");
        if (body.status !== convo.status) {
          convo.status = body.status;
          const who = agentName(userId) || "An agent";
          if (body.status === "Closed" || body.status === "Resolved") convo.messages.push(msg("system", `${who} closed this conversation.`, { public: true }));
        }
      }
      if (body.priority !== undefined) {
        if (!PRIORITIES.includes(body.priority)) return validationFail({ priority: "Invalid priority." });
        convo.priority = body.priority;
      }
      convo.updatedAt = nowIso();
      conversationsStore.save(all);
      return ok(200, convoView(convo));
    }
  },
  {
    method: "POST", pattern: /^\/api\/conversations\/([\w-]+)\/claim$/, permission: "conversations:write",
    handler: ({ tenantId, userId, match }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      if (convo.assignedAgentId && convo.assignedAgentId !== userId) return fail(409, "ALREADY_CLAIMED", "This conversation is already assigned.");
      if (convo.assignedAgentId !== userId) {
        convo.assignedAgentId = userId;
        convo.messages.push(msg("system", `${agentName(userId)} joined the conversation.`, { public: true }));
      }
      convo.unread = 0;
      convo.updatedAt = nowIso();
      conversationsStore.save(all);
      return ok(200, convoView(convo));
    }
  },
  {
    method: "POST", pattern: /^\/api\/conversations\/([\w-]+)\/assign$/, permission: "conversations:write",
    handler: ({ tenantId, match, body }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      if (body.agentId) {
        const agent = usersStore.all().find((u) => u.id === body.agentId && u.tenantId === tenantId);
        if (!agent || agent.status !== "Active" || agent.teamRole === "Viewer") {
          return validationFail({ agentId: "Choose an active agent from this workspace." }, "Choose an active agent from this workspace.");
        }
        if (convo.assignedAgentId !== agent.id) convo.messages.push(msg("system", `Assigned to ${agent.name}`, { public: true }));
        convo.assignedAgentId = agent.id;
      } else {
        convo.assignedAgentId = null;
      }
      convo.updatedAt = nowIso();
      conversationsStore.save(all);
      return ok(200, convoView(convo));
    }
  },
  {
    method: "POST", pattern: /^\/api\/conversations\/([\w-]+)\/messages$/, permission: "conversations:write",
    handler: ({ tenantId, userId, role, match, body }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      const textError = lengthError(body.text, { min: 1, max: 2000, label: "Message" });
      if (textError) return validationFail({ text: textError }, textError === "Message is required." ? "Message cannot be empty." : textError);
      if (convo.assignedAgentId && convo.assignedAgentId !== userId && role !== "admin") {
        return fail(409, "NOT_ASSIGNED_TO_YOU", "This conversation is assigned to another agent.");
      }
      if (!convo.assignedAgentId) {
        convo.assignedAgentId = userId; // replying implicitly claims an unassigned conversation
        convo.messages.push(msg("system", `${agentName(userId)} joined the conversation.`, { public: true }));
      }
      const message = msg("agent", String(body.text).trim(), { agentName: agentName(userId) });
      convo.messages.push(message);
      convo.unread = 0;
      convo.updatedAt = nowIso();
      conversationsStore.save(all);
      return ok(201, { message, conversation: convoView(convo) });
    }
  },
  {
    method: "POST", pattern: /^\/api\/conversations\/([\w-]+)\/notes$/, permission: "conversations:write",
    handler: ({ tenantId, userId, match, body }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      const noteError = lengthError(body.text, { min: 1, max: 1000, label: "Note" });
      if (noteError) return validationFail({ text: noteError }, noteError);
      const note = { id: genId("note"), text: String(body.text).trim(), createdAt: nowIso(), author: agentName(userId) };
      convo.notes = [...(convo.notes || []), note];
      conversationsStore.save(all);
      return ok(201, note);
    }
  },
  {
    method: "POST", pattern: /^\/api\/conversations\/([\w-]+)\/tags$/, permission: "conversations:write",
    handler: ({ tenantId, match, body }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      const name = String(body.name || "").trim();
      const tagError = lengthError(name, { min: 1, max: 30, label: "Tag" }) || (/^[\w .-]+$/.test(name) ? "" : "Tags may contain only letters, numbers, spaces, - and _.");
      if (tagError) return validationFail({ name: tagError }, tagError);
      if ((convo.tags || []).some((t) => t.name.toLowerCase() === name.toLowerCase())) return fail(409, "TAG_EXISTS", "This tag already exists on the conversation.");
      const tag = { id: genId("tag"), name };
      convo.tags = [...(convo.tags || []), tag];
      conversationsStore.save(all);
      return ok(201, tag);
    }
  },
  {
    method: "DELETE", pattern: /^\/api\/conversations\/([\w-]+)\/tags\/([\w-]+)$/, permission: "conversations:write",
    handler: ({ tenantId, match }) => {
      const { all, convo } = findConvo(tenantId, match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      convo.tags = (convo.tags || []).filter((t) => String(t.id) !== match[2]);
      conversationsStore.save(all);
      return ok(200, { tagId: match[2] });
    }
  },

  // ---------------------------------------------------------------- public visitor widget
  // A real website visitor has no ChatBizz login: these routes are auth:false and resolve the bot
  // from the id in the body (or, inside the admin console demo, from the workspace header).
  {
    method: "GET", pattern: /^\/api\/widget\/bot$/, permission: null, tenant: false, auth: false,
    handler: ({ tenantId, query }) => {
      const botId = query.get("botId");
      const bot = botId ? botsStore.all().find((b) => b.id === botId) : (tenantId ? defaultBotFor(tenantId) : null);
      if (!bot) return fail(404, "NOT_FOUND", "No chatbot is configured for this workspace.");
      return ok(200, publicBot(bot));
    }
  },
  {
    method: "POST", pattern: /^\/api\/widget\/start$/, permission: null, tenant: false, auth: false,
    handler: ({ tenantId, body }) => {
      const bots = botsStore.all();
      const bot = body.botId ? bots.find((b) => b.id === body.botId) : (tenantId ? defaultBotFor(tenantId) : null);
      if (!bot) return fail(404, "NOT_FOUND", "Bot not found.");
      if (body.origin && !hostAllowed(bot.tenantId, body.origin)) {
        return fail(403, "DOMAIN_NOT_ALLOWED", "This website is not on the workspace's allowed-domain list.");
      }
      const stamp = nowIso();
      const convo = {
        id: genId("wc"), tenantId: bot.tenantId, botId: bot.id, customer: `Visitor ${randomCode().slice(0, 4)}`, channel: "Web chat",
        status: "Open", unread: 0, createdAt: stamp, updatedAt: stamp, priority: "low", assignedAgentId: null,
        visitorName: "", visitorEmail: "", leadId: null, fallbackStreak: 0, tags: [], notes: [],
        messages: [msg("bot", bot.appearance?.welcome || bot.greeting)]
      };
      const all = conversationsStore.all();
      conversationsStore.save([...all, convo]);
      return ok(201, {
        conversationId: convo.id, bot: publicBot(bot), conversation: widgetView(convo),
        suggestedQuestions: ["Pricing Plans", "Book a Demo", "Technical Support"]
      });
    }
  },
  {
    method: "GET", pattern: /^\/api\/widget\/([\w-]+)$/, permission: null, tenant: false, auth: false,
    handler: ({ match }) => {
      const convo = conversationsStore.all().find((c) => c.id === match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      return ok(200, widgetView(convo));
    }
  },
  {
    method: "POST", pattern: /^\/api\/widget\/([\w-]+)\/messages$/, permission: null, tenant: false, auth: false,
    handler: ({ match, body }) => {
      const all = conversationsStore.all();
      const convo = all.find((c) => c.id === match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      const text = String(body.message || "").trim();
      if (!text) return validationFail({ message: "Enter a message." });
      if (text.length > 1000) return validationFail({ message: "Messages can be at most 1000 characters." }, "Messages can be at most 1000 characters.");
      if (convo.status === "Closed" || convo.status === "Resolved") return fail(409, "CONVERSATION_CLOSED", "This conversation has ended. Start a new chat.");

      convo.messages.push(msg("visitor", text));
      convo.unread = (convo.unread || 0) + 1;
      convo.updatedAt = nowIso();

      // A human owns an escalated/assigned chat: no bot auto-reply that could contradict the agent.
      if (convo.status === "Escalated" || convo.assignedAgentId) {
        conversationsStore.save(all);
        return ok(201, widgetView(convo));
      }

      const bot = botsStore.all().find((b) => b.id === convo.botId);
      if (!consumeAiReply(convo.tenantId, convo.botId).allowed) {
        // Monthly AI quota used up: hand the visitor to a person instead of leaving them without an answer.
        escalate(convo, {
          reason: "ai_quota", systemText: "Escalated — this workspace has used its AI replies for the month",
          botText: "I'm connecting you with a human agent, who will be with you shortly."
        });
        conversationsStore.save(all);
        return ok(201, widgetView(convo));
      }
      const result = simulateBotReply(bot, convo, text);
      if (result.visitorName) {
        convo.visitorName = result.visitorName;
        if (!convo.leadId) convo.customer = result.visitorName;
      }
      convo.fallbackStreak = result.matched ? 0 : (convo.fallbackStreak || 0) + 1;

      if (result.escalate) {
        escalate(convo, { reason: result.reason, systemText: "Escalated — visitor requested a human agent", botText: result.reply });
      } else if (convo.fallbackStreak >= 2 && bot?.config?.handoff?.enabled !== false) {
        escalate(convo, {
          reason: "unresolved", systemText: "Escalated — bot could not resolve after 2 unanswered questions",
          botText: "I'm not able to help with that confidently — I've connected you with a human agent, who will be with you shortly."
        });
      } else {
        convo.messages.push(msg("bot", result.reply, { meta: { matched: result.matched, reason: result.reason, truncated: result.truncated } }));
      }
      conversationsStore.save(all);
      return ok(201, widgetView(convo));
    }
  },
  {
    method: "POST", pattern: /^\/api\/widget\/([\w-]+)\/lead$/, permission: null, tenant: false, auth: false,
    handler: ({ match, body }) => {
      const all = conversationsStore.all();
      const convo = all.find((c) => c.id === match[1]);
      if (!convo) return fail(404, "NOT_FOUND", "Conversation not found.");
      const name = String(body.name || "").trim();
      const email = String(body.email || "").trim().toLowerCase();
      const bot = botsStore.all().find((b) => b.id === convo.botId);
      const form = leadCaptureFor(bot);
      const phone = form.phone === "off" ? "" : String(body.phone || "").trim();
      const company = form.company ? String(body.company || "").trim() : "";
      const message = form.message ? String(body.message || "").trim() : "";
      const fields = collect([
        ["name", nameError(name)], ["email", emailError(email)],
        ["phone", form.phone === "required" && !phone ? "Phone number is required." : phoneError(phone)],
        company ? ["company", lengthError(company, { max: 80, label: "Company" })] : null,
        message ? ["message", lengthError(message, { max: 500, label: "Message" })] : null
      ].filter(Boolean));
      if (fields) return validationFail(fields, firstMessage(fields));

      const intent = detectIntent(convo.messages);
      const visitorMessages = convo.messages.filter((m) => m.from === "visitor").length;
      const { lead, duplicate } = createLeadRecord(convo.tenantId, {
        name, email, phone, company, message, source: "Website", intent, score: scoreLead({ intent, visitorMessages }),
        timelineFirst: "Lead created via Website chat"
      });
      convo.customer = name;
      convo.visitorName = name;
      convo.visitorEmail = email;
      convo.leadId = lead.id;
      convo.messages.push(msg("bot", `Thanks, ${name}! Your contact details have been saved for this session.`));
      convo.updatedAt = nowIso();
      conversationsStore.save(all);
      return ok(201, { lead, duplicate, conversation: widgetView(convo) });
    }
  }
];
